import { useState, useEffect } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import {
  useSavingsGoals,
  useSavingsContributions,
  type SavingsGoal,
  type SavingsContribution,
  type CreateGoalInput,
  type UpdateGoalInput,
} from '@/hooks';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Shield,
  Plane,
  Car,
  Home,
  GraduationCap,
  Gift,
  Wallet,
  Target,
  ChevronDown,
  ChevronUp,
  type LucideIcon,
} from 'lucide-react';

// ─── Icon Presets ────────────────────────────────────────────────────────

const ICON_OPTIONS: { value: string; label: string; Icon: LucideIcon }[] = [
  { value: 'shield', label: 'Emergency', Icon: Shield },
  { value: 'plane', label: 'Travel', Icon: Plane },
  { value: 'car', label: 'Vehicle', Icon: Car },
  { value: 'home', label: 'Home', Icon: Home },
  { value: 'graduation-cap', label: 'Education', Icon: GraduationCap },
  { value: 'gift', label: 'Gift', Icon: Gift },
  { value: 'wallet', label: 'Savings', Icon: Wallet },
  { value: 'target', label: 'Goal', Icon: Target },
];

function getIconComponent(iconValue: string | null): LucideIcon {
  const found = ICON_OPTIONS.find((o) => o.value === iconValue);
  return found?.Icon ?? Target;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatCurrencyDecimal(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function computeMonthlyNeeded(goal: SavingsGoal): string | null {
  if (!goal.target_date || goal.status !== 'active') return null;

  const remaining = parseFloat(goal.target_amount) - parseFloat(goal.current_amount);
  if (remaining <= 0) return null;

  const today = new Date();
  const target = new Date(goal.target_date + 'T00:00:00');
  const monthsLeft =
    (target.getFullYear() - today.getFullYear()) * 12 +
    (target.getMonth() - today.getMonth());

  if (monthsLeft <= 0) return 'Overdue';
  return `${formatCurrency(Math.ceil(remaining / monthsLeft))}/mo to stay on track`;
}

// ─── Types ───────────────────────────────────────────────────────────────

type DialogMode = 'create' | 'edit' | 'contribute' | 'editContribution' | null;

interface GoalFormState {
  name: string;
  target_amount: string;
  description: string;
  icon: string;
  color: string;
  target_date: string;
}

const EMPTY_FORM: GoalFormState = {
  name: '',
  target_amount: '',
  description: '',
  icon: 'target',
  color: '#22C55E',
  target_date: '',
};

interface ContributionFormState {
  amount: string;
  notes: string;
  contributed_at: string;
}

const EMPTY_CONTRIBUTION: ContributionFormState = {
  amount: '',
  notes: '',
  contributed_at: todayISO(),
};

// ─── ContributionHistory ────────────────────────────────────────────────

function ContributionHistory({
  goalId,
  onEdit,
  onDelete,
}: {
  goalId: string;
  onEdit: (contribution: SavingsContribution) => void;
  onDelete: (contribution: SavingsContribution) => void;
}) {
  const { contributions, isLoading } = useSavingsContributions(goalId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (contributions.length === 0) {
    return (
      <p className="py-3 text-center text-sm text-muted-foreground">
        No contributions recorded yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {contributions.map((c) => (
        <div
          key={c.id}
          className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm"
        >
          <div>
            <span className="font-medium">
              {new Date(c.contributed_at + 'T00:00:00').toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
            {c.notes && (
              <span className="ml-2 text-muted-foreground">— {c.notes}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-sapling-primary-100">
              +{formatCurrencyDecimal(parseFloat(c.amount))}
            </span>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(c)}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onDelete(c)}>
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── GoalCard ────────────────────────────────────────────────────────────

function GoalCard({
  goal,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onContribute,
  onEditContribution,
  onDeleteContribution,
}: {
  goal: SavingsGoal;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onContribute: () => void;
  onEditContribution: (contribution: SavingsContribution) => void;
  onDeleteContribution: (contribution: SavingsContribution) => void;
}) {
  const current = parseFloat(goal.current_amount);
  const target = parseFloat(goal.target_amount);
  const percentage = target > 0 ? Math.min((current / target) * 100, 100) : 0;
  const monthlyNeeded = computeMonthlyNeeded(goal);
  const GoalIcon = getIconComponent(goal.icon);

  return (
    <div className="border-b border-border py-5 last:border-b-0">
      {/* Header row */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ backgroundColor: goal.color ? `${goal.color}20` : '#22C55E20' }}
          >
            <GoalIcon
              className="h-5 w-5"
              style={{ color: goal.color || '#22C55E' }}
            />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{goal.name}</h3>
            {goal.description && (
              <p className="text-sm text-muted-foreground">{goal.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {/* Amount + progress */}
      <div className="mb-2 flex items-baseline justify-between text-sm">
        <span className="text-muted-foreground">
          {formatCurrency(current)} saved of {formatCurrency(target)} goal
        </span>
        <span className="font-medium text-foreground">{Math.round(percentage)}%</span>
      </div>

      <div className="mb-3 h-2.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-sapling-primary-100 transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Target date + monthly needed */}
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
        {goal.target_date && (
          <span>
            Target: {new Date(goal.target_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
        )}
        {monthlyNeeded && (
          <span className={monthlyNeeded === 'Overdue' ? 'text-destructive font-medium' : ''}>
            {monthlyNeeded}
          </span>
        )}
      </div>

      {/* Actions row */}
      <div className="flex items-center gap-2">
        {goal.status === 'active' && (
          <Button variant="outline" size="sm" onClick={onContribute}>
            <Plus className="mr-1 h-4 w-4" />
            Add Contribution
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onToggle}>
          {isExpanded ? (
            <>
              <ChevronUp className="mr-1 h-4 w-4" />
              Hide History
            </>
          ) : (
            <>
              <ChevronDown className="mr-1 h-4 w-4" />
              Contribution History
            </>
          )}
        </Button>
      </div>

      {/* Expanded contribution history */}
      {isExpanded && (
        <div className="mt-4 rounded-lg border bg-background p-4">
          <h4 className="mb-3 text-sm font-semibold text-foreground">Contribution History</h4>
          <ContributionHistory
            goalId={goal.id}
            onEdit={onEditContribution}
            onDelete={onDeleteContribution}
          />
        </div>
      )}
    </div>
  );
}

// ─── SavingsGoalsPage ────────────────────────────────────────────────────

export function SavingsGoalsPage() {
  const {
    goals, isLoading, createGoal, updateGoal, deleteGoal,
    addContribution, updateContribution, deleteContribution,
  } = useSavingsGoals({ includeCompleted: true });

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editingGoal, setEditingGoal] = useState<SavingsGoal | null>(null);
  const [contributingGoalId, setContributingGoalId] = useState<string | null>(null);
  const [form, setForm] = useState<GoalFormState>(EMPTY_FORM);
  const [contribForm, setContribForm] = useState<ContributionFormState>(EMPTY_CONTRIBUTION);
  const [editingContribution, setEditingContribution] = useState<SavingsContribution | null>(null);
  const [expandedGoalId, setExpandedGoalId] = useState<string | null>(null);

  const isSaving =
    createGoal.isPending || updateGoal.isPending || addContribution.isPending ||
    updateContribution.isPending || deleteContribution.isPending;

  // Auto-open create dialog if ?action=add is in URL
  const searchParams = useSearch({ strict: false }) as Record<string, string>;
  useEffect(() => {
    if (searchParams.action === 'add') {
      setForm(EMPTY_FORM);
      setEditingGoal(null);
      setDialogMode('create');
    }
  }, []);

  // ── Active vs completed goals
  const activeGoals = goals.filter((g) => g.status === 'active');
  const completedGoals = goals.filter((g) => g.status !== 'active');

  // ── Dialog handlers
  function openCreate() {
    setForm(EMPTY_FORM);
    setEditingGoal(null);
    setDialogMode('create');
  }

  function openEdit(goal: SavingsGoal) {
    setForm({
      name: goal.name,
      target_amount: parseFloat(goal.target_amount).toString(),
      description: goal.description || '',
      icon: goal.icon || 'target',
      color: goal.color || '#22C55E',
      target_date: goal.target_date || '',
    });
    setEditingGoal(goal);
    setDialogMode('edit');
  }

  function openContribute(goalId: string) {
    setContributingGoalId(goalId);
    setContribForm(EMPTY_CONTRIBUTION);
    setDialogMode('contribute');
  }

  function openEditContribution(goalId: string, contribution: SavingsContribution) {
    setContributingGoalId(goalId);
    setEditingContribution(contribution);
    setContribForm({
      amount: parseFloat(contribution.amount).toString(),
      notes: contribution.notes || '',
      contributed_at: contribution.contributed_at,
    });
    setDialogMode('editContribution');
  }

  function closeDialog() {
    setDialogMode(null);
    setEditingGoal(null);
    setContributingGoalId(null);
    setEditingContribution(null);
  }

  async function handleSave() {
    const amount = parseFloat(form.target_amount);
    if (!form.name.trim()) {
      toast({ variant: 'destructive', title: 'Name is required' });
      return;
    }
    if (isNaN(amount) || amount <= 0) {
      toast({ variant: 'destructive', title: 'Target amount must be a positive number' });
      return;
    }

    try {
      if (dialogMode === 'create') {
        const input: CreateGoalInput = {
          name: form.name.trim(),
          target_amount: amount,
          description: form.description.trim() || undefined,
          icon: form.icon || undefined,
          color: form.color || undefined,
          target_date: form.target_date || undefined,
        };
        await createGoal.mutateAsync(input);
        toast({ title: 'Goal created' });
      } else if (dialogMode === 'edit' && editingGoal) {
        const input: UpdateGoalInput & { id: string } = {
          id: editingGoal.id,
          name: form.name.trim(),
          target_amount: amount,
          description: form.description.trim() || null,
          icon: form.icon || null,
          color: form.color || null,
          target_date: form.target_date || null,
        };
        await updateGoal.mutateAsync(input);
        toast({ title: 'Goal updated' });
      }
      closeDialog();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  async function handleContribute() {
    const amount = parseFloat(contribForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast({ variant: 'destructive', title: 'Amount must be a positive number' });
      return;
    }
    if (!contributingGoalId) return;

    try {
      const result = await addContribution.mutateAsync({
        id: contributingGoalId,
        amount,
        notes: contribForm.notes.trim() || undefined,
        contributed_at: contribForm.contributed_at || undefined,
      });
      const wasCompleted = result.status === 'completed';
      toast({
        title: wasCompleted ? 'Goal completed!' : 'Contribution added',
        description: wasCompleted
          ? 'Congratulations! You reached your savings goal.'
          : `Added ${formatCurrency(amount)} to your goal.`,
      });
      closeDialog();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  async function handleUpdateContribution() {
    const amount = parseFloat(contribForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast({ variant: 'destructive', title: 'Amount must be a positive number' });
      return;
    }
    if (!contributingGoalId || !editingContribution) return;

    try {
      await updateContribution.mutateAsync({
        goalId: contributingGoalId,
        contributionId: editingContribution.id,
        amount,
        notes: contribForm.notes.trim() || null,
        contributed_at: contribForm.contributed_at,
      });
      toast({ title: 'Contribution updated' });
      closeDialog();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  async function handleDeleteContribution(goalId: string, contribution: SavingsContribution) {
    if (!confirm('Delete this contribution? The goal\'s saved amount will be reduced.')) return;
    try {
      await deleteContribution.mutateAsync({
        goalId,
        contributionId: contribution.id,
      });
      toast({ title: 'Contribution deleted' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  async function handleDelete(goal: SavingsGoal) {
    if (!confirm(`Delete "${goal.name}"? This cannot be undone.`)) return;
    try {
      await deleteGoal.mutateAsync(goal.id);
      toast({ title: 'Goal deleted' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  // ── Totals
  const totalSaved = activeGoals.reduce((sum, g) => sum + parseFloat(g.current_amount), 0);
  const totalTarget = activeGoals.reduce((sum, g) => sum + parseFloat(g.target_amount), 0);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 py-8">
        <div className="container max-w-3xl">
          {/* Back link */}
          <Link
            to="/app/personal/dashboard"
            className="mb-6 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Personal Dashboard
          </Link>

          {/* Page header */}
          <div className="mb-8 flex items-center justify-between">
            <h1 className="text-2xl font-bold text-foreground">Savings Goals</h1>
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Create Goal
            </Button>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Empty state */}
          {!isLoading && goals.length === 0 && (
            <div className="mt-12 rounded-card border border-dashed bg-muted/50 p-8 text-center">
              <Target className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <p className="mb-1 font-medium text-foreground">No savings goals yet</p>
              <p className="mb-4 text-sm text-muted-foreground">
                Create your first goal to start tracking your savings.
              </p>
              <Button onClick={openCreate} variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Create Goal
              </Button>
            </div>
          )}

          {/* Active goals */}
          {!isLoading && activeGoals.length > 0 && (
            <div className="rounded-card border bg-card p-6">
              {activeGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  isExpanded={expandedGoalId === goal.id}
                  onToggle={() => setExpandedGoalId(expandedGoalId === goal.id ? null : goal.id)}
                  onEdit={() => openEdit(goal)}
                  onDelete={() => handleDelete(goal)}
                  onContribute={() => openContribute(goal.id)}
                  onEditContribution={(c) => openEditContribution(goal.id, c)}
                  onDeleteContribution={(c) => handleDeleteContribution(goal.id, c)}
                />
              ))}

              {/* Total summary */}
              {activeGoals.length > 1 && (
                <div className="mt-4 flex items-center justify-between border-t border-border pt-4 text-sm font-medium">
                  <span className="text-muted-foreground">
                    Total: {formatCurrency(totalSaved)} of {formatCurrency(totalTarget)}
                  </span>
                  <span className="text-foreground">
                    {totalTarget > 0 ? Math.round((totalSaved / totalTarget) * 100) : 0}% overall
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Completed / cancelled goals */}
          {!isLoading && completedGoals.length > 0 && (
            <div className="mt-8">
              <h2 className="mb-4 text-lg font-semibold text-muted-foreground">
                Completed & Cancelled
              </h2>
              <div className="rounded-card border bg-card p-6 opacity-75">
                {completedGoals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    isExpanded={expandedGoalId === goal.id}
                    onToggle={() => setExpandedGoalId(expandedGoalId === goal.id ? null : goal.id)}
                    onEdit={() => openEdit(goal)}
                    onDelete={() => handleDelete(goal)}
                    onContribute={() => {}}
                    onEditContribution={(c) => openEditContribution(goal.id, c)}
                    onDeleteContribution={(c) => handleDeleteContribution(goal.id, c)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ── Create / Edit Dialog ─────────────────────────────────────── */}
      <Dialog
        open={dialogMode === 'create' || dialogMode === 'edit'}
        onOpenChange={(open) => { if (!open) closeDialog(); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'create' ? 'Create Savings Goal' : 'Edit Savings Goal'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="goal-name">Name</Label>
              <Input
                id="goal-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Emergency Fund"
              />
            </div>

            <div>
              <Label htmlFor="goal-target">Target Amount ($)</Label>
              <Input
                id="goal-target"
                type="number"
                min="0.01"
                step="0.01"
                value={form.target_amount}
                onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
                placeholder="10000"
              />
            </div>

            <div>
              <Label htmlFor="goal-desc">Description (optional)</Label>
              <Input
                id="goal-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Save for 6 months of expenses"
              />
            </div>

            <div>
              <Label>Icon</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {ICON_OPTIONS.map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setForm({ ...form, icon: value })}
                    className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                      form.icon === value
                        ? 'border-sapling-primary-100 bg-sapling-primary-100/10 text-sapling-primary-100'
                        : 'border-border hover:bg-muted'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="goal-color">Color</Label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="color"
                  id="goal-color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="h-9 w-9 cursor-pointer rounded border-0 bg-transparent p-0"
                />
                <Input
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-28 font-mono text-sm"
                  maxLength={7}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="goal-date">Target Date (optional)</Label>
              <Input
                id="goal-date"
                type="date"
                value={form.target_date}
                onChange={(e) => setForm({ ...form, target_date: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {dialogMode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add / Edit Contribution Dialog ────────────────────────────── */}
      <Dialog
        open={dialogMode === 'contribute' || dialogMode === 'editContribution'}
        onOpenChange={(open) => { if (!open) closeDialog(); }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'editContribution' ? 'Edit Contribution' : 'Add Contribution'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="contrib-amount">Amount ($)</Label>
              <Input
                id="contrib-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={contribForm.amount}
                onChange={(e) => setContribForm({ ...contribForm, amount: e.target.value })}
                placeholder="100"
                autoFocus
              />
            </div>

            <div>
              <Label htmlFor="contrib-date">Date</Label>
              <Input
                id="contrib-date"
                type="date"
                value={contribForm.contributed_at}
                onChange={(e) => setContribForm({ ...contribForm, contributed_at: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="contrib-notes">Notes (optional)</Label>
              <Input
                id="contrib-notes"
                value={contribForm.notes}
                onChange={(e) => setContribForm({ ...contribForm, notes: e.target.value })}
                placeholder="Monthly deposit"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={dialogMode === 'editContribution' ? handleUpdateContribution : handleContribute}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {dialogMode === 'editContribution' ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
